import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { baueAktivitaeten } from '../../../../../lib/immobilien/aktivitaet';
import { schluesselJeEinheit } from '../../../../../lib/immobilien/schluessel';
import { kontaktName } from '../../../../../lib/objekte/objektKontakte';
import { TicketsBereich } from '../../gemeinsam/TicketsBereich';
import { EtvBereich, VersicherungBereich, VertraegeBereich, WartungBereich } from '../../gemeinsam/FachBereiche';
import { AktivitaetBereich, MieterhoehungBereich, SchluesselBereich } from '../../gemeinsam/WeitereBereiche';
import { useEinheitSeite } from '../EinheitSeitenKontext';

function useObjektPanel() {
  const { daten } = useEinheitSeite();
  const navigate = useNavigate();
  return (panel: string, bearbeiten = false) =>
    navigate(`/admin/objekte/${encodeURIComponent(daten.objekt.id)}?panel=${panel}${bearbeiten ? '&bearbeiten=1' : ''}`);
}

function useMieterName() {
  const { daten } = useEinheitSeite();
  return useMemo(() => {
    const m = new Map(daten.mieter.map(x => [x.id, kontaktName(x)]));
    return (id: string | null) => (id ? m.get(id) ?? null : null);
  }, [daten.mieter]);
}

export function TicketsEinheit() {
  const s = useEinheitSeite();
  return <TicketsBereich tickets={s.daten.tickets} objektId={s.daten.objekt.id} einheitId={s.daten.einheit.id} mieterId={s.aktuell?.id ?? null} onGeaendert={() => s.geaendert('tickets')} />;
}

export function WartungEinheit() {
  const s = useEinheitSeite();
  const zumObjekt = useObjektPanel();
  return (
    <WartungBereich fuerEinheit objektId={s.daten.objekt.id} letztePruefung={s.daten.objekt.letzte_pruefung ?? null} termine={s.daten.termine}
      dokumente={s.daten.fachDokumente} dienstleister={s.daten.dienstleister} heute={s.heute}
      onTerminGeaendert={() => s.neuLaden('termine')} onPruefungAendern={() => zumObjekt('stamm', true)} />
  );
}

export function VersicherungEinheit() {
  const { daten } = useEinheitSeite();
  return <VersicherungBereich fuerEinheit objektId={daten.objekt.id} dokumente={daten.fachDokumente} dienstleister={daten.dienstleister} />;
}

export function VertraegeEinheit() {
  const { daten } = useEinheitSeite();
  const zumObjekt = useObjektPanel();
  const name = useMieterName();
  return (
    <VertraegeBereich fuerEinheit vertraege={daten.vertraege} dienstleister={daten.dienstleister} links={daten.dienstleisterLinks}
      mieterName={id => name(id) ?? 'Mieter'} onDienstleister={() => zumObjekt('dienstleister')} />
  );
}

export function EtvEinheit() {
  const s = useEinheitSeite();
  return <EtvBereich fuerEinheit objektId={s.daten.objekt.id} termine={s.daten.termine} heute={s.heute} onTerminGeaendert={() => s.neuLaden('termine')} />;
}

export function SchluesselEinheit() {
  const s = useEinheitSeite();
  const name = useMieterName();
  const staende = useMemo(() => [...schluesselJeEinheit(s.daten.uebergaben).values()], [s.daten.uebergaben]);
  return <SchluesselBereich staende={staende} einheiten={[{ id: s.daten.einheit.id, bezeichnung: s.bezeichnung }]} mieterName={name} />;
}

export function AktivitaetEinheit() {
  const s = useEinheitSeite();
  const name = useMieterName();
  const liste = useMemo(() => baueAktivitaeten({
    notizen: s.daten.notizen,
    dokumente: s.daten.dokumente,
    tickets: s.daten.tickets,
    uebergaben: s.daten.uebergaben,
    mieter: s.daten.mieter.map(m => ({ id: m.id, name: kontaktName(m), mietbeginn: m.mietbeginn, mietende: m.mietende })),
    audit: s.daten.audit.map(a => ({ ...a, name: name(a.datensatz_id) ?? undefined })),
  }, new Date().toISOString()), [s.daten, name]);
  return <AktivitaetBereich aktivitaeten={liste} />;
}

export function MieterhoehungEinheit() {
  const s = useEinheitSeite();
  const zeilen = s.laufende.map(m => ({
    id: m.id, einheit: s.bezeichnung, mieter: kontaktName(m), flaeche: s.flaeche, roh: m, onBearbeiten: () => s.bearbeiteMietverhaeltnis(m, 'miete'),
  }));
  return <MieterhoehungBereich zeilen={zeilen} heute={s.heute} einzeln />;
}
